<template>
    <div @click="scrollToSelected">
        <div v-if="label" class="label" :style="coloredLabel ? `color: var(--v-${color}-base)` : ''"> {{ label }}</div>
        <v-combobox v-if="canWrite || searchable" :color="color" :item-color="color" v-model="model"
            :items="itemsAvailable" :placeholder="placeholder" :item-text="itemText" :item-value="itemValue"
            disable-lookup solo flat :dense="dense"
            :prepend-inner-icon="icon" :clearable="clearable" @click:clear="$emit('clear')" @change="onSelected"
            hide-details :class="['kimsa-elevation', { 'placeholder-text': placeholderClass }]"
            :height="height" :return-object="!noObject" :no-data-text="$t('common.not_found')"
            :multiple="multipleWithChips" :readonly="readonly" :search-input.sync="search" @blur="$emit('blur')"
            :loading="loading" ref="select" :no-filter="noFilter" append-icon="mdi-chevron-down">
            <template v-if="multipleWithChips" v-slot:selection="{ item, index }">
                <div v-if="index === 0" class="text-truncate" style="width: 90%">
                    {{ item[itemText] || item.name }}
                    <span v-if="model.length > 1" class="caption">
                        (+{{ model.length - 1 }})
                    </span>
                </div>
            </template>
            <template v-slot:no-data>
                <v-list-item link @click="createNew(search)">
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ canWrite && !noCreateExercise ? $t('routines.no_data_create') : $t('common.not_found') }}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </template>
            <template v-if="showVideo || withListIcon || !multipleWithChips" v-slot:item="{ active, item, attrs, on }">
                <v-list-item v-if="showVideo" v-on="on" v-bind="attrs" class="item-pointer">
                    <v-list-item-action @click="onClickItem(item, $event)">
                        <v-img class="item-thumb" height="40" width="40" :src="thumbImg(item) || imgPlaceholder"
                            :lazy-src="imgPlaceholder"></v-img>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title>
                            <v-row no-gutters align="center">
                                <span>{{ item.name }}</span>
                            </v-row>
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item v-else-if="withListIcon" v-on="on" v-bind="attrs" class="item-pointer">
                    <v-list-item-action>
                        <v-avatar size="20" :color="item.hasInjury ? 'error' : 'accent'">
                            <v-icon color="#FFFFFF" small>{{ item.leftIcon }}</v-icon>
                        </v-avatar>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title>
                            <v-row no-gutters align="center">
                                <span>{{ item.name }}</span>
                            </v-row>
                        </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <v-avatar size="20" :color="item.rightIcon.color">
                            <v-icon color="#FFFFFF" small>{{ item.rightIcon.icon }}</v-icon>
                        </v-avatar>
                    </v-list-item-action>
                </v-list-item>
                <v-list-item v-else v-on="on" v-bind="attrs" class="dot-item">
                    <span class="sel-dot" :class="{ 'sel-dot--active': isSelected(item) }"></span>
                    <span class="sel-name" :class="{ 'sel-name--active': isSelected(item) }">
                        {{ (item && (item[itemText] || item.name)) || item }}
                    </span>
                </v-list-item>
            </template>
        </v-combobox>
        <v-select v-else :color="color" :item-color="color" v-model="model" :items="itemsAvailable"
            :placeholder="placeholder" :item-text="itemText" :item-value="itemValue" disable-lookup
            solo flat :dense="dense"
            :prepend-inner-icon="icon" :clearable="clearable" @click:clear="$emit('clear')" @change="onSelected"
            hide-details :class="['kimsa-elevation', { 'placeholder-text': placeholderClass }]"
            :height="height" :return-object="!noObject" :no-data-text="$t('common.not_found')"
            :multiple="multipleWithChips" :readonly="readonly" :menu-props="menuProps" ref="select"
            :append-outer-icon="appendIcon" @click:append-outer="$emit('click:append-outer')" append-icon="mdi-chevron-down">
            <template v-if="multipleWithChips && showCloseButton" v-slot:append-item>
                <div class="append">
                    <v-list-item link @click="$refs.select.blur()">
                        <v-list-item-action>
                            <v-icon>mdi-close</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>
                                <span>{{ $t('common.close') }}</span>
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </div>
            </template>
            <template v-if="multipleWithChips" v-slot:selection="{ item, index }">
                <div v-if="index === 0" class="text-truncate" style="width: 90%">
                    {{ item[itemText] || item.name }}
                    <span v-if="model.length > 1" class="caption">
                        (+{{ model.length - 1 }})
                    </span>
                </div>
            </template>
            <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-if="multipleWithChips" v-on="on" v-bind="attrs" class="check-item">
                    <v-simple-checkbox :color="color" :value="isSelected(item)" :ripple="false" class="check-box" />
                    <span class="sel-name" :class="{ 'sel-name--active': isSelected(item) }">
                        {{ (item && (item[itemText] || item.name)) || item }}
                    </span>
                </v-list-item>
                <v-list-item v-else v-on="on" v-bind="attrs" class="dot-item">
                    <span class="sel-dot" :class="{ 'sel-dot--active': isSelected(item) }"></span>
                    <span class="sel-name" :class="{ 'sel-name--active': isSelected(item) }">
                        {{ (item && (item[itemText] || item.name)) || item }}
                    </span>
                </v-list-item>
            </template>
        </v-select>

        <div v-if="withInfo && array.length" class="kimsa-tags">
            <div class="kimsa-tag" v-for="item in array" :key="item.value">
                <span class="kimsa-tag-text">{{ item.label || item.name }}</span>
                <v-icon class="kimsa-tag-close" size="15" @click="remove(item)">mdi-close</v-icon>
            </div>
        </div>

    </div>
</template>

<script>
import isEqual from 'lodash/isEqual'

export default {
    name: "KimsaCombobox",
    props: {
        color: {
            type: String,
            default: 'secondary'
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        items: {
            type: Array,
            default: []
        },
        multiple: {
            type: Boolean,
            default: false
        },
        withInfo: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        solo: {
            type: Boolean,
            default: false
        },
        placeholderClass: {
            type: Boolean,
            default: false
        },
        multipleWithChips: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        coloredLabel: {
            type: Boolean,
            default: false
        },
        itemText: {
            type: String,
            default: 'name'
        },
        itemValue: {
            type: String,
            default: 'value'
        },
        noObject: {
            type: Boolean,
            default: false
        },
        canWrite: {
            type: Boolean,
            default: false,
        },
        showVideo: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Number, String, Object, Array]
        },
        icon: {
            type: String,
            default: undefined,
        },
        height: {
            type: [Number, String],
            default: undefined,
        },
        showCloseButton: {
            type: Boolean,
            default: false,
        },
        maxSelected: {
            type: [String, Number],
            default: null,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        withListIcon: {
            type: Boolean,
            default: false,
        },
        noCreateExercise: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        limitCharts: {
            type: Boolean,
            default: false,
        },
        appendIcon: {
            type: String,
            default: null,
        },
        noFilter: {
            type: Boolean,
            default: false,
        },
        withSelectedDot: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {
        this.syncModelFromValue(this.value)
    },
    data() {
        return {
            model: null,
            array: [],
            search: null,
        }
    },
    computed: {
        itemsAvailable(vm = this) {
            if (vm.array.length === 0 && (!vm.multiple || !vm.multipleWithChips)) return vm.items
            if (vm.items.length === 0) return []
            return vm.items.filter(el => !vm.simpleArraySelected.includes(el.name))
        },
        simpleArraySelected(vm = this) {
            if (vm.array.length === 0) return []
            return vm.array.map(item => item.name)
        },
        menuProps(vm = this) {
            const contentClass = `kimsa-select-menu${vm.showCloseButton ? ' kimsa-select-menu--append' : ''}`
            return { offsetY: true, nudgeBottom: 8, contentClass }
        },
    },
    methods: {
        syncModelFromValue(value) {
            if (isEqual(value, this.model)) return
            if (Array.isArray(value)) return this.model = [...value]
            if (this.multipleWithChips && value !== null && value !== undefined) return this.model = [value]
            this.model = value
        },
        isSelected(item) {
            const vm = this
            const m = vm.model
            if (m === null || m === undefined) return false
            const matches = (sel) => {
                if (item && typeof item === 'object') {
                    if (sel && typeof sel === 'object') {
                        if (item.name != null && sel.name === item.name) return true
                        const iv = item[vm.itemValue]
                        return iv != null && sel[vm.itemValue] === iv
                    }
                    return sel === item[vm.itemValue] || sel === item.name
                }
                return sel === item
            }
            if (Array.isArray(m)) return m.some(matches)
            return matches(m)
        },
        onSelected() {
            var vm = this

            let currentLength = vm.model && vm.model.length || 0
            if (this.maxSelected && Number(this.maxSelected) < currentLength) {
                vm.model.pop()
                return this.toast(vm.$t('common.max_selection'), 'error')
            }

            if (vm.limitCharts) {
                if (vm.model.includes('time_rpe_charge')) {
                    vm.model = [vm.model[currentLength - 1]]
                } else if (currentLength > 3) {
                    vm.model = vm.model.slice(0, 3);
                    vm.toast(vm.$t('common.max_selection'), 'error')
                }
                return
            }

            if (!vm.model || !vm.multiple) return

            vm.array.push(vm.model)

            vm.$nextTick(() => {
                vm.model = null
            })
        },
        remove(item) {
            var vm = this

            if (vm.withInfo) {
                vm.array.forEach((el, i) => {
                    if (el.name === item.name) {
                        vm.array.splice(i, 1)
                    }
                })
            }
        },
        setDefaults(array) {
            const vm = this
            vm.array = array.map(item => {
                return {
                    name: item,
                    value: item,
                }
            })
        },
        createNew(val) {
            const vm = this
            if (!vm.canWrite || vm.noCreateExercise) return
            vm.$nextTick(() => {
                vm.search = null
                vm.model = null
                vm.$refs.select.blur()
            })
            vm.$emit('create', val)
        },
        onClickItem(item, event) {
            const vm = this
            if ((!vm.thumbImg(item) || !item.video) && !item.youtubeUrl) return
            if (event) event.stopPropagation()
            vm.$emit('showVideo', item)
        },
        thumbImg(item) {
            const vm = this
            if (item.youtubeUrl) return item.img || vm.getYoutubeImgUrl(item.youtubeUrl)
            return item.img || (item.video && item.video.thumb) || null
        },
        scrollToSelected() {
            const vm = this
            if (vm.multiple || vm.multipleWithChips) return
            setTimeout(() => {
                const content = vm.$refs.select?.$refs?.menu?.$refs?.content
                const selected = content?.querySelector('.v-list-item[aria-selected="true"]')
                if (!selected) return
                content.scrollTop = selected.offsetTop - (content.clientHeight / 2) + (selected.clientHeight / 2)
            }, 60)
        },
        closeMenu() {
            const vm = this
            if (vm.$refs.select) {
                vm.$refs.select.isFocused = false
                vm.$refs.select.isMenuActive = false
            }
        },
        openMenu() {
            const vm = this
            if (vm.$refs.select) {
                vm.$refs.select.isFocused = true
                vm.$refs.select.isMenuActive = true
            }
        },
    },
    watch: {
        value(val) {
            this.syncModelFromValue(val)
        },
        model(val) {
            if (this.canWrite) {
                if (val && typeof val === 'object') {
                    return this.$emit('change', val)
                } else {
                    return
                }
            }
            if (!this.multiple) {
                this.$emit('change', val)
            }
        },
        array(val) {
            if (this.multiple || this.multipleWithChips) {
                this.$emit('change', val)
            }
        },
        search(val) {
            if (!this.model) this.$emit('search', val)
        }
    }
}
</script>

<style scoped>
.label {
    display: block;
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.kimsa-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
}

.kimsa-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 32px;
    padding: 0 8px 0 12px;
    border-radius: 8px;
    background: rgba(102, 204, 204, 0.12);
    border: 1px solid var(--v-secondary-base);
    color: var(--v-accent-base);
    font-size: 13px;
    font-weight: 600;
}

.kimsa-tag-close {
    cursor: pointer;
    color: var(--v-txtMuted-base) !important;
    transition: color .15s ease;
}

.kimsa-tag-close:hover {
    color: var(--v-secondary-base) !important;
}

.equipment-box {
    border: 1px solid #999999;
    border-radius: 4px;
    padding: 5px
}

.placeholder-text {
    font-size: var(--fs-input);
    color: var(--v-txtMuted-base);
}

::v-deep .v-select__selection,
::v-deep input {
    font-size: var(--fs-input);
}

::v-deep .v-input__icon--clear {
    opacity: 1 !important;
}

::v-deep .v-input__icon--clear .v-icon {
    color: var(--v-txtMuted-base) !important;
}

::v-deep .v-input__icon--clear .v-icon:hover {
    color: var(--v-txtSecondary-base) !important;
}

::v-deep .v-select.v-input--dense .v-select__selection--comma {
    margin: auto !important;
}

.v-select__slot .v-select__selections {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.item-pointer {
    cursor: pointer;
}

.item-thumb {
    border-radius: 8px;
    flex-shrink: 0;
}

.item-pointer:hover {
    background-color: rgba(0, 0, 0, 0.03) !important;
}

.v-list-item--active {
    background-color: rgba(102, 204, 204, 0.1) !important;
}

.v-list-item--active:hover {
    background-color: rgba(102, 204, 204, 0.1) !important;
}

::v-deep .v-select__selections {
    flex-wrap: nowrap;
}

.append {
    position: sticky;
    bottom: 0;
    z-index: 1;
    background: var(--v-surface-base);
    border-top: 1px solid var(--v-outline-base);
}
</style>

<style>
.kimsa-select-menu {
    overflow-y: auto !important;
    overflow-x: hidden !important;
}

.kimsa-select-menu--append .v-list {
    padding-bottom: 0;
}

.kimsa-select-menu .check-item {
    min-height: 42px;
}

.kimsa-select-menu .check-box {
    margin-right: 10px;
    flex-shrink: 0;
    pointer-events: none;
}

.dot-item {
    min-height: 42px;
}

.dot-item .sel-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 12px;
    flex-shrink: 0;
    background: transparent;
}

.dot-item .sel-dot--active {
    background: var(--v-secondary-base);
}

.sel-name {
    font-size: var(--fs-body);
}

.sel-name--active {
    color: var(--v-secondary-base);
    font-weight: 600;
}
</style>